import { Fragment, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Outlet } from "react-router-dom";
import { observer } from "mobx-react-lite";
import AppFrame from "./AppFrame";
import { useStore } from "../stores/store";
import { Loading } from "@shopify/polaris";
import LoginPage from "../../features/users/LoginPage";
import LandingPage from "../../features/home/LandingPage";
import { hasShopifyAuthParams, stripShopifyAuthParams } from "../utils/shopifyAuth";
import { rememberLastAppRoute } from "../utils/lastAppRoute";
import ProductFitmentCopyActivity from "../../features/products/ProductFitmentCopyActivity";
import AppNavMenu from "./AppNavMenu";
import { isEmbeddedAdminFrame } from "../components/SafeAppBridge";

const App = () => {
  const { commonStore, userStore } = useStore();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const loadUser = async () => {
      if (hasShopifyAuthParams(location.search)) {
        if (commonStore.token || userStore.isLoggedIn) {
          const nextSearch = stripShopifyAuthParams(location.search);
          const nextUrl = `${location.pathname}${nextSearch}${location.hash}`;
          if (nextUrl !== `${location.pathname}${location.search}${location.hash}`) {
            navigate(nextUrl, { replace: true });
          }

          if (commonStore.token && !userStore.isLoggedIn) {
            await userStore.getUser();
          }

          commonStore.setAppLoaded();
          return;
        }

        // Shopify launch params are authoritative. Re-run embedded login so
        // offline tokens and granted scopes refresh after app scope changes.
        commonStore.setToken(null);
        commonStore.setAppLoaded();
        return;
      }

      if (!commonStore.token) {
        commonStore.setAppLoaded();
        return;
      }

      await userStore.getUser();

      const params = new URLSearchParams(window.location.search);
      const urlShop = params.get("shop");
      if (urlShop && userStore.user?.shopDomain && urlShop !== userStore.user.shopDomain) {
        userStore.logout();
      }

      commonStore.setAppLoaded();
    };

    loadUser();
  }, [commonStore, location.hash, location.pathname, location.search, navigate, userStore]);

  useEffect(() => {
    if (userStore.isLoggedIn) {
      rememberLastAppRoute(location.pathname, location.search, location.hash);
    }
  }, [location.hash, location.pathname, location.search, userStore.isLoggedIn]);

  const isShopifyLaunch = hasShopifyAuthParams(location.search);
  const showAppNav = isEmbeddedAdminFrame();

  if (!commonStore.appLoaded) return (
    <Fragment>
      {showAppNav && <AppNavMenu />}
      <AppFrame><div style={{ height: '100px' }}><Loading /></div></AppFrame>
    </Fragment>
  )

  if (!userStore.isLoggedIn) {
    if (window.location.pathname === '/login' || isShopifyLaunch) {
      return (
        <Fragment>
          {showAppNav && <AppNavMenu />}
          <LoginPage />
        </Fragment>
      );
    }

    return (
      <Fragment>
        <LandingPage />
      </Fragment>
    );
  }

  return (
    <Fragment>
      {showAppNav && <AppNavMenu />}
      <AppFrame>
        <ProductFitmentCopyActivity />
        <Outlet />
      </AppFrame>
    </Fragment>
  )

};

export default observer(App);
